import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import { connect } from 'react-redux';
import { isEqual, isEmpty } from 'lodash';
import { loadSchedule, requestEmployee, requestIQM } from 'actionCreators';
import { getEmployeeById, getManageVendorWorkrooms } from '../../../../../reducers';
import CalendarActionBar from 'components/CalendarActionBar';
import CalendarLegend from 'calendarLegend';
import CalendarHeader from 'calendarHeader';
import CalendarFilters from 'calendarFilters';
import SchedulingCalendar from 'schedulingCalendar';
import CalendarJumpDate from 'calendarJumpDate';
import ColoredButton from 'components/ColoredButton';
import { AppointmentType } from 'types/appointment';
import { CalendarFilterType } from 'types/filter';
import ScheduleType from 'types/schedule';
import S from 'calendar.scss';
import cx from 'classnames';
import { NUM_DAYS_MOBILE, NUM_DAYS_DESKTOP, DEFAULT_MVW } from 'config/constants';

class CalendarContainer extends Component {
  constructor(props) {
    super(props);
    this.onShowMismatchedCrewsClick = this.onShowMismatchedCrewsClick.bind(this);
    const { filters, role, isScheduleBoard, applySmartScheduling } = this.props;
    this.state = {
      showSsFilters: applySmartScheduling && !isScheduleBoard,
      showMismatchedCrews: false,
      stateFilters: filters,
      emptyResultsMsg: !['installer', 'estimator'].includes(role) && isScheduleBoard ?
        "Check filters then click 'Load Schedule' button for results." :
        'There are no appointments listed for the filter you have applied.',
    };
  }

  componentWillMount = () => {
    const { startDate, days, filters, isScheduleBoard, isCcs, defaultCalStart = null,
            employableId = null, role = '', username = '', pendingAppointment = null,
            applySmartScheduling, phaseSource } = this.props;
    const calendarArgs = { days, isScheduleBoard };
    if (!['installer', 'estimator'].includes(role) && isScheduleBoard) {
      this.loadMembers(startDate, filters, {}, { emptyMembers: true });
    }
    if (!['installer', 'estimator', 'iqm'].includes(role)) {
      this.props.requestEmployee(employableId).then(() => {
        if ((filters.workroom.length || []).length === 0) this.determineWorkroomStateValue();
      });
    }
    if (role === 'iqm') {
      this.props.requestIQM(employableId).then(() => {
        if ((filters.workroom.length || []).length === 0) this.determineWorkroomStateValue();
      });
    }
    if (!isScheduleBoard) {
      this.loadMembers(startDate || new Date(), filters, calendarArgs, {
        defaultCalStart,
        role,
        username,
        pendingAppointment,
        applySmartScheduling,
        isCcs,
        phaseSource,
      });
    }
    if (isScheduleBoard && ['installer', 'estimator'].includes(role)) {
      this.loadMembers(startDate || new Date(), filters, calendarArgs,
        { defaultCalStart, role, username, pendingAppointment });
    }
  };

  componentWillReceiveProps = (newProps) => {
    const { startDate, days, filters, isScheduleBoard, selectedDayStart } = newProps;
    if (!isEqual(this.props.filters, filters) ||
        this.props.startDate.getTime() !== startDate.getTime() ||
        this.props.days !== days ||
        (!isScheduleBoard && selectedDayStart !== this.props.selectedDayStart)) {
      const calendarArgs = { days, isScheduleBoard };
      let addtlArgs = {};
      if (!isScheduleBoard) {
        const { defaultCalStart, role, username, pendingAppointment,
                applySmartScheduling, isCcs, phaseSource } = newProps;
        addtlArgs = {
          defaultCalStart,
          role,
          username,
          pendingAppointment,
          applySmartScheduling,
          isCcs,
          phaseSource,
        };
      }
      if (!isEqual(this.props.startDate, startDate) ||
         (!isScheduleBoard && !isEqual(this.props.filters, filters))) {
        this.loadMembers(startDate, filters, calendarArgs, addtlArgs);
      }
    }
  }

  onLoadScheduleClick = (newFilters) => {
    const { updateScheduleFilters, updateFilters, startDate, days, isScheduleBoard } = this.props;
    const calendarArgs = { days, isScheduleBoard };
    updateScheduleFilters(newFilters).then(() =>
      this.loadMembers(startDate, newFilters, calendarArgs, {})).then(() =>
        (this.state.emptyResultsMsg.includes('Load Schedule') ?
          this.setState({ emptyResultsMsg: 'There are no appointments listed for the filter you have applied.' },
          () => updateFilters(newFilters)) : updateFilters(newFilters)));
  }

  onShowMismatchedCrewsClick = () =>
    this.setState({ showMismatchedCrews: !this.state.showMismatchedCrews });

  handleScheduleFilterChange = (key, values) =>
    this.setState({ stateFilters: { ...this.state.stateFilters, [key]: values } });

  determineWorkroomStateValue = () => {
    const { employee, iqm } = this.props;
    if (!isEmpty(employee)) {
      this.handleScheduleFilterChange('workroom', employee.workrooms.map(w => w.id));
    }
    if (!isEmpty(iqm)) {
      this.handleScheduleFilterChange('workroom', iqm.workrooms.map(w => w.id));
    }
  }

  loadMembers = (startDate, filters, calendarArgs, addtlArgs) =>
    this.props.loadSchedule(startDate, filters, true, calendarArgs, addtlArgs);

  render = () => {
    const { isDesktop, navigatePast, navigateFuture, scrolling, title, toggleFilters,
            currentPage, currentTab, filters, handleJumpDate, dates, calDates, headerClassName,
            active, onFilterChange, filterClassName, onClose, theme, isScheduleBoard,
            handleDeliveryValidation, schedule, setDelayedField, applySmartScheduling,
            startDate, days, selectable, selectedDayStart, selectedDayEnd, selectedAssignee,
            onSelect, pendingAppointment, role, onOpeningClick, relatedPhases, phaseSource,
            phaseZip, calRef, isCcs, manageVendorWorkrooms, employableType, phaseType,
            phaseEstimatorId, phaseInstallerId, noSticky,
            schedule: { loading } } = this.props;
    const { stateFilters, emptyResultsMsg, showSsFilters, showMismatchedCrews } = this.state;
    // IFI phase scheduling gets the mismatch toggle too (it never had it for
    // Assessment/Workorder before), but specificMismatch filtering must NOT apply to IFI —
    // IFI visibility is governed solely by the assigned estimator/installer match.
    const isIfiPhaseSchedule = phaseSource === 'IFI' && !isScheduleBoard &&
      ['Assessment', 'Installation', 'Workorder'].includes(phaseType);
    const showMismatchToggle = showSsFilters || isIfiPhaseSchedule;
    const applySsFilters = showSsFilters && phaseSource !== 'IFI';
    return (<div>
      <CalendarActionBar
        showAdd={false}
        title={title}
        toggleFilters={loading ? () => {} : toggleFilters}
        currentPage={noSticky ? '' : currentPage}
        currentTab={noSticky ? '' : currentTab}
        role={role}
      >
        <CalendarJumpDate
          filters={filters}
          handleJumpDate={loading ? () => {} : handleJumpDate}
        />
        {isScheduleBoard && !['installer', 'estimator'].includes(role) && <ColoredButton
          className={S.loadButton}
          responsive
          color="green"
          label="Load Schedule"
          disabled={(stateFilters.workroom || []).length === 0}
          onClick={isScheduleBoard ? () => this.onLoadScheduleClick(stateFilters) :
            () => this.loadMembers(startDate, stateFilters, { days, isScheduleBoard }, {})}
        />}
        {isDesktop ? <CalendarLegend /> : <CalendarHeader
          currentPage={currentPage}
          dates={dates.slice(0, NUM_DAYS_MOBILE)}
          navigatePast={loading ? () => {} : navigatePast(NUM_DAYS_MOBILE)}
          navigateFuture={loading ? () => {} : navigateFuture(NUM_DAYS_MOBILE)}
          className={cx(headerClassName, {
            [S.inAppointments]: currentTab === 'appointments',
            [S.inSchedule]: currentPage === 'schedule',
            [S.scrolling]: scrolling,
          })}
        />}
      </CalendarActionBar>
      <CalendarFilters
        role={role}
        active={active}
        phaseType={phaseType}
        phaseSource={phaseSource}
        onFilterChange={isScheduleBoard ? this.handleScheduleFilterChange : onFilterChange}
        filters={isScheduleBoard ? stateFilters : filters}
        className={filterClassName}
        onClose={loading ? () => {} : onClose}
        theme={theme}
        handleDeliveryValidation={handleDeliveryValidation}
        showSsFilters={showMismatchToggle}
        showMismatchedCrews={showMismatchedCrews}
        onShowMismatchedCrewsClick={this.onShowMismatchedCrewsClick}
      />
      <SchedulingCalendar
        dates={calDates}
        emptyResultsMsg={emptyResultsMsg}
        startDate={startDate}
        days={days}
        selectable={selectable}
        selectedDayStart={selectedDayStart}
        selectedDayEnd={selectedDayEnd}
        selectedAssignee={selectedAssignee}
        onSelect={onSelect}
        pendingAppointment={pendingAppointment}
        role={role}
        isScheduleBoard={isScheduleBoard}
        applySmartScheduling={applySmartScheduling}
        onOpeningClick={onOpeningClick}
        relatedPhases={relatedPhases}
        schedule={schedule}
        setDelayedField={setDelayedField}
        phaseZip={phaseZip}
        phaseType={phaseType}
        phaseSource={phaseSource}
        phaseEstimatorId={phaseEstimatorId}
        phaseInstallerId={phaseInstallerId}
        isCcs={isCcs}
        manageVendorWorkrooms={manageVendorWorkrooms}
        employableType={employableType}
        applySsFilters={applySsFilters}
        showMismatchedCrews={showMismatchedCrews}
      >
        {isDesktop && <CalendarHeader
          ref={calRef}
          dates={dates.slice(0, NUM_DAYS_DESKTOP)}
          navigatePast={loading ? () => {} : navigatePast(NUM_DAYS_DESKTOP)}
          navigateFuture={loading ? () => {} : navigateFuture(NUM_DAYS_DESKTOP)}
          className={cx(headerClassName, {
            [S.inAppointments]: currentTab === 'appointments',
            [S.inSchedule]: currentPage === 'schedule',
            [S.scrolling]: scrolling,
          })}
        />}
      </SchedulingCalendar>
    </div>);
  }
}

CalendarContainer.propTypes = {
  canLoadSchedule: PropTypes.bool,
  setCanLoadSchedule: PropTypes.func,
  loadSchedule: PropTypes.func.isRequired,
  schedule: ScheduleType.isRequired,
  isDesktop: PropTypes.bool,
  navigatePast: PropTypes.func.isRequired,
  navigateFuture: PropTypes.func.isRequired,
  scrolling: PropTypes.string,
  title: PropTypes.string,
  toggleFilters: PropTypes.func,
  currentPage: PropTypes.string,
  currentTab: PropTypes.string,
  filters: CalendarFilterType,
  handleJumpDate: PropTypes.func,
  dates: PropTypes.arrayOf(PropTypes.instanceOf(Moment)).isRequired,
  headerClassName: PropTypes.string,
  active: PropTypes.bool.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  filterClassName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  theme: PropTypes.shape({
    RTDrawer: PropTypes.string,
    RTList: PropTypes.string,
    RTOverlay: PropTypes.string,
  }),
  isScheduleBoard: PropTypes.bool,
  applySmartScheduling: PropTypes.bool,
  handleDeliveryValidation: PropTypes.func,
  calDates: PropTypes.arrayOf(PropTypes.instanceOf(Moment)).isRequired,
  startDate: PropTypes.instanceOf(Date).isRequired,
  defaultCalStart: PropTypes.instanceOf(Date),
  days: PropTypes.number.isRequired,
  selectable: PropTypes.bool,
  selectedDayStart: PropTypes.instanceOf(Moment),
  selectedDayEnd: PropTypes.instanceOf(Moment),
  selectedAssignee: PropTypes.shape({
    id: PropTypes.number,
    kind: PropTypes.string,
  }),
  onSelect: PropTypes.func,
  pendingAppointment: AppointmentType,
  role: PropTypes.string,
  username: PropTypes.string,
  employableId: PropTypes.number,
  onOpeningClick: PropTypes.func,
  relatedPhases: PropTypes.array,
  calRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  setDelayedField: PropTypes.func,
  phaseZip: PropTypes.string,
  phaseType: PropTypes.string,
  phaseSource: PropTypes.string,
  phaseEstimatorId: PropTypes.number,
  phaseInstallerId: PropTypes.number,
  isCcs: PropTypes.bool,
  requestEmployee: PropTypes.func,
  requestIQM: PropTypes.func,
  employee: PropTypes.object,
  iqm: PropTypes.object,
  onLoadScheduleClick: PropTypes.func,
  updateScheduleFilters: PropTypes.func,
  updateFilters: PropTypes.func,
  manageVendorWorkrooms: PropTypes.object,
  employableType: PropTypes.string,
  noSticky: PropTypes.bool,
};

CalendarContainer.defaultProps = {
  title: 'Calendar',
  dates: [],
  date: new Date(),
  onFilterChange: () => { },
  filters: {},
  navigatePast: () => { },
  navigateFuture: () => { },
  selectable: false,
  isScheduleBoard: false,
  relatedPhases: [],
  manageVendorWorkrooms: DEFAULT_MVW,
};

const mapStateToProps = (state, ownProps) => {
  const { role, employableId } = ownProps;
  const employee = role && !['Estimator', 'Installer', 'iqm'].includes(role) ? getEmployeeById(state)[Number(employableId)] : {};
  const iqm = role && role === 'iqm' ? state.iqms.details : {};
  return {
    schedule: state.schedule,
    manageVendorWorkrooms: getManageVendorWorkrooms(state),
    employableType: state.user.employable_type,
    employee,
    iqm,
  };
};

export default connect(mapStateToProps, {
  loadSchedule,
  requestEmployee,
  requestIQM,
})(CalendarContainer);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/schedule/components/calendarContainer.jsx